/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    overrideTitle = "overrideTitle",
    cssClass = "cssClass",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.overrideTitle]: string;
        [fields.cssClass]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface PageTitleStyles {
    wrapper?: InjectableCss;
}

export const pageTitleStyles: PageTitleStyles = {
    wrapper: {
        css: css`
            width: 100%;
            height: 100%;
            &.topBanner {
                position: fixed;
                top: 0px;
                left: 0px;
                height: 100px;
                max-width: 100%;
                > div {
                    justify-content: center;
                    display: flex;
                    align-items: center;
                }
                h1 {
                    margin: 0;
                    font-weight: normal;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-size: 26px;
                            `,
                            css`
                                font-size: 26px;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
            }
        `,
    },
};

const styles = pageTitleStyles;

const PageTitle: FC<Props> = ({ fields, pageTitle }) => {
    let backgroundStyles;
    if (fields.background === "image") {
        backgroundStyles = `background-image: url(${fields.image});
        background-size: cover;`;
    } else if (fields.background === "color") {
        backgroundStyles = `background-color: ${fields.backgroundColor};
        border-bottom: 5px solid rgb(3, 110, 183);`;
    } else {
        backgroundStyles = "";
    }

    const textColorStyles = fields.background ? `color: ${fields.textColor};` : `color: ${getColor("text.main")};`;

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
        `,
    };

    const textStyles = {
        css: css`
            ${textColorStyles}
        `,
    };

    return (
        <StyledWrapper className={fields.cssClass} {...styles.wrapper}>
            <StyledWrapper {...wrapperStyles}>
                <Typography variant="h1" as="h1" data-test-selector="PageTitle" {...textStyles}>
                    {fields.overrideTitle || pageTitle}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PageTitle),
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "black",
            },
            {
                fieldType: "Translatable",
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                displayName: "Override Title",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
